import { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faArrowDown,
  faArrowUp,
  faTriangleExclamation,
  faCoins,
} from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from "../../utils";

/* ---------------- WRAPPER ---------------- */

const Wrapper = styled.section`
  width: 56px;
  height: 100%;
  position: relative;
  border-left: 1px solid #e5e7eb;
`;

/* ---------------- BUTTON ---------------- */

const IconButton = styled.button`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    font-size: 18px;
    color: #6b7280;
  }

  &:hover {
    background: #f3f4f6;
  }

  &.active {
    background: #b05408;

    svg {
      color: #fff;
    }
  }

  &::after {
    content: "";
    position: absolute;
    top: 14px;
    right: 14px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => (p.$lowBalance ? "#ef4444" : "#22c55e")};
    display: ${(p) => (p.$hasBalance ? "block" : "none")};
    animation: ${(p) => (p.$lowBalance ? "blink 1s infinite" : "none")};
  }

  @keyframes blink {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.3; transform: scale(1.4); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

/* ---------------- DROPDOWN ---------------- */

const Dropdown = styled.div`
  position: absolute;
  top: 56px;
  right: 0;
  width: 280px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0,0,0,0.08);
  overflow: hidden;
  z-index: 100;
`;

/* ---------------- HEADER ---------------- */

const Header = styled.div`
  background: #b05408;
  color: #fff;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* ---------------- WARNING BANNER ---------------- */

const WarningBanner = styled.div`
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  padding: 10px 12px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const WarningText = styled.div`
  font-size: 12px;
  color: #991b1b;

  b {
    display: block;
    margin-bottom: 2px;
  }
`;

/* ---------------- ROWS ---------------- */

const Row = styled.div`
  padding: 12px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Value = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

/* ---------------- COMPONENT ---------------- */

const WalletSection = ({ gstBuyerWallet, gstSellerWallet }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const toggle = (e) => {
    e.preventDefault();
    setOpen((p) => !p);
  };

  const close = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", close, true);
    return () => document.removeEventListener("click", close, true);
  }, [close]);

  const buyer = Number(gstBuyerWallet || 0);
  const seller = Number(gstSellerWallet || 0);

  const hasBalance = buyer > 0 || seller > 0;
  const isLowSellerBalance = seller < 500;

  return (
    <Wrapper ref={ref}>
      <IconButton
        onClick={toggle}
        className={open ? "active" : ""}
        $hasBalance={hasBalance}
        $lowBalance={isLowSellerBalance}
      >
        <FontAwesomeIcon icon={faWallet} />
      </IconButton>

      {open && (
        <Dropdown>
          <Header>
            <FontAwesomeIcon icon={faCoins} />
            Wallet Balance
          </Header>

          {/* 🔴 LOW BALANCE BANNER */}
          {isLowSellerBalance && (
            <WarningBanner>
              <FontAwesomeIcon icon={faTriangleExclamation} color="#ef4444" />
              <WarningText>
                <b>Low Seller Wallet Balance</b>
                Please top-up to avoid transaction failures.
              </WarningText>
            </WarningBanner>
          )}

          <Row>
            <Left>
              <FontAwesomeIcon icon={faArrowDown} color="#16a34a" />
              <Label>Buyer Wallet</Label>
            </Left>
            <Value>{formatCurrency(buyer)}</Value>
          </Row>

          <Row>
            <Left>
              <FontAwesomeIcon icon={faArrowUp} color="#2563eb" />
              <Label>Seller Wallet</Label>
            </Left>
            <Value>{formatCurrency(seller)}</Value>
          </Row>
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default WalletSection;